import { AppError } from '../errors/AppError';
import { CGU } from '../components/CGU';
import { Captcha } from '../components/Captcha';
import { DeviceDetect } from '../utils/DeviceDetect';
import { Exists } from '../components/Exists';
import { Identity } from '../components/Identity';
import { Polling } from '../components/Polling';
import { PollingResult } from '../components/PollingResult';
import { Expire } from '../components/Expire';
import { SessionService } from '../services/SessionService';
import { ProbeService } from '../services/ProbeService';
import { Scroll } from '../utils/Scroll';
import { Url } from '../utils/Url';
import { Delete } from '../components/Delete';
import { DeleteSend } from '../components/DeleteSend';
import { DeleteUnknownUser } from '../components/DeleteUnknownUser';
import { GDPR } from '../components/GDPR';
import { Footer } from '../components/Footer';
import { Security } from '../components/Security';
import { TechnicalError } from '../components/TechnicalError';

/**
 * Router Object.
 */
export class Router {
  static currentRoute = '';

  static hashHistory = [];

  static historyLength = 0;

  static expireComponentName = 'expire';

  static Routes = {
    cgu: {
      component: () => new CGU('#stage'),
    },
    gdpr: {
      component: () => new GDPR('#stage'),
    },
    captcha: {
      component: () => new Captcha('#stage'),
    },
    identity: {
      component: () => new Identity('#stage'),
    },
    security: {
      component: () => new Security('#stage'),
    },
    confirmation: {
      component: () => new PollingResult('#stage'),
    },
    polling: {
      component: () => new Polling('#modal-container'),
    },
    exists: {
      component: () => new Exists('#stage'),
    },
    delete: {
      component: () => new Delete('#stage'),
    },
    deleteSend: {
      component: () => new DeleteSend('#stage'),
    },
    deleteUnknownUser: {
      component: () => new DeleteUnknownUser('#stage'),
    },
    pollingTimeout: {
      component: () => new PollingResult('#stage'),
    },
    [Router.expireComponentName]: {
      component: () => new Expire('#stage'),
    },
    technicalError: {
      component: () => new TechnicalError('#stage'),
    },
  };

  /**
   * Insert - stage in parameter.
   *
   * @param   {string} stage - Stage in parameter.
   */
  static init(stage) {
    // Init History API
    Router.hashHistory = [window.location.hash];
    Router.historyLength = window.history.length;

    // Init common component for the interface ( for back button && step bar)
    Router.initCommonUI();

    // Add window events for the router
    Router.addWindowEvents();

    // Insert the first Stage
    Router.insertStage({
      stage,
    });
  }

  /**
   * Init the CommonUI Component for the application.
   */
  static initCommonUI() {
    Footer.initListener();
  }

  /**
   * Forward Event.
   *
   * @returns {boolean} If the window event is a Back or Forward navigation event.
   */
  static isBackForward() {
    return window.performance && window.performance.navigation && window.performance.navigation.type === window.performance.navigation.TYPE_BACK_FORWARD;
  }

  /**
   * Reload Event.
   *
   * @returns {boolean} If the window event is a Reload navigation event.
   */
  static isReload() {
    return window.performance && window.performance.navigation && window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;
  }

  /**
   * Reload the page.
   */
  static reload() {
    window.location.reload();
  }

  /**
   * Assign the new string to location.
   *
   * @param {string} string - The new uri.
   */
  static assign(string) {
    window.location.assign(string);
  }

  /**
   * Add window events in order to listen and handle routing of the application.
   */
  static addWindowEvents() {
    window.onpageshow = (event) => {
      if (event.persisted || Router.isBackForward()) {
        Router.reload();
      }
    };

    window.onhashchange = () => Router.detectBackOrForward();

    // Disable BF Cache From Firefox and Safari in order to 'reload' (document.ready) the page when using the back button. Note : it is not disabling the HTTP Cache from any browser including firefox.
    // https://developer.mozilla.org/en-US/docs/Working_with_BFCache
    window.onunload = function () {
      if (!DeviceDetect.isIE()) {
        return ProbeService.sendProbe();
      }
    };
  }

  /**
   * Detect back or forward in function of a hash history.
   */
  static detectBackOrForward() {
    const { hash } = window.location;
    let length;

    // History length is not initialized the same way in IE 9.
    if (DeviceDetect.isIE() === 9) {
      length = window.history.length - 1;
    } else {
      ({ length } = window.history);
    }

    // Emulate history Api
    if (Router.hashHistory.length && Router.historyLength === length) {
      // Emulate the popstate from history API ( remove a state from the array )
      // For Back action
      // Example ['#identity', '#security', '#identity']
      if (Router.hashHistory[Router.hashHistory.length - 2] === hash) {
        // Slice use instead of pop
        Router.hashHistory = Router.hashHistory.slice(0, -1);
        // Callback for on Back function
        Router.onBackAction();
      } else {
        // For Forward action
        // Emulate the pushstate from history API ( add a state from the array )
        Router.hashHistory.push(hash);
        // Callback for on Forward function
        Router.onForwardAction();
      }
    } else {
      // Init state for History Api if history is empty
      Router.hashHistory.push(hash);
      Router.historyLength = length;
    }
  }

  /**
   * On back browser button action.
   */
  static onBackAction() {
    // if the hash is different from the current route
    // Back action from browser because the currente route and hash is different
    if (window.location.hash.indexOf(Router.currentRoute) === -1) {
      Router.back();
    }
  }

  /**
   * On forward browser button action.
   */
  static onForwardAction() {
    // if the hash is different from the current route
    // Forward action from browser because the currente route and hash is different
    if (window.location.hash.indexOf(Router.currentRoute) === -1) {
      window.history.back();
    }
  }

  /**
   * Get Route from location hash.
   *
   * @returns {string} String route.
   */
  static getRoute() {
    return Router.currentRoute;
  }

  /**
   * Set Route.
   *
   * @param {string} route - The route to set.
   */
  static setRoute(route) {
    Router.currentRoute = route;
    Router.setLocationHash(`/${route}`);
  }

  /**
   * Set Location Hash.
   *
   * @param {string} hash - The hash to set.
   */
  static setLocationHash(hash) {
    window.location.hash = hash;
  }

  /**
   * Insert Template.
   *
   * @param   {object} opts - Options in order to insert stage.
   */
  static insertStage(opts) {
    if (opts) {
      const stage = Router.Routes[opts.stage];

      if (stage) {
        Router.currentComponent = stage.component();
        Router.currentComponent.render(opts);

        // Scroll to the page top after the stage insertion
        // The scroll position could be updated by previous stage (error scroll, user scroll with mobile device)
        AppError.hideDefault();
        Scroll.scrollTop();
        ProbeService.endTimer();
      }
    }
  }

  /**
   * Go back from api history.
   */
  static back() {
    let promiseToResolve = Promise.resolve();

    if (typeof Router.currentComponent.back === 'function') {
      promiseToResolve = Router.currentComponent.back() || Promise.resolve();
    } else {
      // If a method back is not present, it means that Router.back was called when the back browser needs to be disabled on the current step.
      // So a forward history action is done in order to cancel the back history action
      window.history.forward();
    }

    return promiseToResolve;
  }

  /**
   * Close the web view mode in adding some query string parameters in url.
   * This function reloads the page only if the web view mode is enabled.
   *
   * @param {object} opts - The objects to encrypt.
   */
  static closeApp(opts) {
    if (Eui.isWebViewMode) {
      // query string parameters are caught by SDK
      const parameters = {
        webview_action: 'closewebview',
      };

      // id attribute represents the encrypted login mail
      if (opts && opts.id) {
        parameters.id = opts.id;
      }
      const url = Url.replaceParameters(window.location.href, parameters);

      if (url !== window.location.href) {
        Router.assign(url);
      }
    } else if (opts && opts.reload) {
      Router.reload();
    } else if (opts && opts.redirectUrl) {
      Router.assign(opts.redirectUrl);
    } else {
      Router.assign(decodeURIComponent(Eui.returnUrl));
    }
  }

  /**
   * Insert session error template.
   */
  static insertExpire() {
    Router.insertStage({
      stage: Router.expireComponentName,
      view: SessionService.template,
    });
  }

  /**
   * Insert technical error template.
   */
  static insertTechnicalError() {
    Router.insertStage({
      stage: 'technicalError',
      view: Eui.technicalErrorTemplate,
    });
  }
}
